import type { Client } from '@urql/core'
import { graphql } from 'app/generated/gql'

import type { Call as CallType } from 'app/generated/graphql'
import { MODEL_FIELDS } from 'app/generated/modelFields'
import { EntityStore } from './EntityStore'

type CallListResult = { hits: CallType[]; total: number; page: number }

const LIST_QUERY = graphql(`
  query Calls($search: String, $pagination: PaginationInput, $sort: SortInput) {
    calls(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        version
        createdDate
        updatedDate
        id
        orgId
        workspaceId
        locationId
        elevenLabsConversationId
        archived
        channel
        vendor
        answerer
        answered
        ai {
          summary
          actionItems
          people {
            name
            treatment
            serviceRequested
            relationship
            appointmentDate
            appointmentTime
            phone
            patientType
            appointmentScheduled
            appointmentRequested
            birthDate
            referral
            insuranceProvider
            outcomes
            reasonForNotScheduling
            isCaller
            index
            email
          }
          answerer
          channel
          extractionDate
          model
          tokenUsage {
            inputTokens
            outputTokens
            totalTokens
          }
        }
        treatment
        patientNames
        callerId
        trackingNumber
        trackingNumberName
        tags
        waveforms
        appointmentDateString
        callerPhone
        businessPhone
        callDate
        note
        status
        recordingDuration
        duration
        utmSource
        utmMedium
        utmCampaign
        utmKeywords
        utmContent
        referringUrl
        gclid
        landingPageUrl
        recordings
        patients {
          name
          treatment
          serviceRequested
          relationship
          appointmentDate
          appointmentTime
          phone
          patientType
          appointmentScheduled
          appointmentRequested
          birthDate
          referral
          insuranceProvider
          outcomes
          reasonForNotScheduling
          isCaller
          index
          email
        }
        staff {
          id
          name
          aliases
          userId
        }
        aiAgent
        golden
        direction
        people {
          name
          treatment
          serviceRequested
          relationship
          appointmentDate
          appointmentTime
          phone
          patientType
          appointmentScheduled
          appointmentRequested
          birthDate
          referral
          insuranceProvider
          outcomes
          reasonForNotScheduling
          isCaller
          index
          email
        }
        allPatientOutcomes
      }
      total
      page
    }
  }
`)

const GET_QUERY = graphql(`
  query Call($id: ID!) {
    call(id: $id) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      locationId
      elevenLabsConversationId
      archived
      channel
      vendor
      answerer
      answered
      ai {
        summary
        actionItems
        people {
          name
          treatment
          serviceRequested
          relationship
          appointmentDate
          appointmentTime
          phone
          patientType
          appointmentScheduled
          appointmentRequested
          birthDate
          referral
          insuranceProvider
          outcomes
          reasonForNotScheduling
          isCaller
          index
          email
        }
        answerer
        channel
        extractionDate
        model
        tokenUsage {
          inputTokens
          outputTokens
          totalTokens
        }
      }
      treatment
      patientNames
      callerId
      trackingNumber
      trackingNumberName
      tags
      waveforms
      appointmentDateString
      callerPhone
      businessPhone
      callDate
      note
      status
      recordingDuration
      duration
      utmSource
      utmMedium
      utmCampaign
      utmKeywords
      utmContent
      referringUrl
      gclid
      landingPageUrl
      recordings
      patients {
        name
        treatment
        serviceRequested
        relationship
        appointmentDate
        appointmentTime
        phone
        patientType
        appointmentScheduled
        appointmentRequested
        birthDate
        referral
        insuranceProvider
        outcomes
        reasonForNotScheduling
        isCaller
        index
        email
      }
      staff {
        id
        name
        aliases
        userId
      }
      aiAgent
      golden
      direction
      people {
        name
        treatment
        serviceRequested
        relationship
        appointmentDate
        appointmentTime
        phone
        patientType
        appointmentScheduled
        appointmentRequested
        birthDate
        referral
        insuranceProvider
        outcomes
        reasonForNotScheduling
        isCaller
        index
        email
      }
      allPatientOutcomes
    }
  }
`)

const UPDATE_MUTATION = graphql(`
  mutation UpdateCall($id: ID!, $input: UpdateCallInput!) {
    updateCall(id: $id, input: $input) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      locationId
      elevenLabsConversationId
      archived
      channel
      vendor
      answerer
      answered
      ai {
        summary
        actionItems
        people {
          name
          treatment
          serviceRequested
          relationship
          appointmentDate
          appointmentTime
          phone
          patientType
          appointmentScheduled
          appointmentRequested
          birthDate
          referral
          insuranceProvider
          outcomes
          reasonForNotScheduling
          isCaller
          index
          email
        }
        answerer
        channel
        extractionDate
        model
        tokenUsage {
          inputTokens
          outputTokens
          totalTokens
        }
      }
      treatment
      patientNames
      callerId
      trackingNumber
      trackingNumberName
      tags
      waveforms
      appointmentDateString
      callerPhone
      businessPhone
      callDate
      note
      status
      recordingDuration
      duration
      utmSource
      utmMedium
      utmCampaign
      utmKeywords
      utmContent
      referringUrl
      gclid
      landingPageUrl
      recordings
      patients {
        name
        treatment
        serviceRequested
        relationship
        appointmentDate
        appointmentTime
        phone
        patientType
        appointmentScheduled
        appointmentRequested
        birthDate
        referral
        insuranceProvider
        outcomes
        reasonForNotScheduling
        isCaller
        index
        email
      }
      staff {
        id
        name
        aliases
        userId
      }
      aiAgent
      golden
      direction
      people {
        name
        treatment
        serviceRequested
        relationship
        appointmentDate
        appointmentTime
        phone
        patientType
        appointmentScheduled
        appointmentRequested
        birthDate
        referral
        insuranceProvider
        outcomes
        reasonForNotScheduling
        isCaller
        index
        email
      }
      allPatientOutcomes
    }
  }
`)

const DELETE_MUTATION = graphql(`
  mutation DeleteCall($id: ID!) {
    deleteCall(id: $id)
  }
`)

export class CallStore extends EntityStore<CallType, CallListResult, undefined, typeof UPDATE_MUTATION> {
  constructor(client: Client) {
    super(client, {
      listQuery: LIST_QUERY,
      getQuery: GET_QUERY,
      updateMutation: UPDATE_MUTATION,
      deleteMutation: DELETE_MUTATION,
      typename: `Call`,
      extractList: (data: any) => data.calls,
      extractOne: (data: any) => data.call,
      extractMutated: (data: any) => data.updateCall,
    })
  }
}
